import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonTextarea,
  IonButton,
  IonSpinner,
  IonText,
  IonSegment,
  IonSegmentButton,
  IonIcon
} from '@ionic/react';
import {
  documentTextOutline,
  alertCircleOutline,
  checkmarkCircleOutline,
  arrowForwardOutline,
  arrowBackOutline,
  downloadOutline
} from 'ionicons/icons';
import config from '../config';
import './PIIWasher.css';

const DEFAULT_BACKEND_URL =
  process.env.REACT_APP_PII_WASHER_BACKEND_URL ||
  'https://pii-washer-api-367524407367.europe-west2.run.app';

const STEPS = [
  { number: 1, label: 'Enter PII' },
  { number: 2, label: 'Upload PDF' },
  { number: 3, label: 'Review PII' },
  { number: 4, label: 'Redact & Download' }
];

const parseLines = (value) => {
  if (!value) return [];
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
};

const bufferToHex = (buffer) => {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const sha256 = async (data) => {
  const encoder = new TextEncoder();
  const encoded = typeof data === 'string' ? encoder.encode(data) : data;
  const digest = await window.crypto.subtle.digest('SHA-256', encoded);
  return bufferToHex(digest);
};

const hashPiiInputs = async (names, dobs, ids, phones, sexes, addresses) => {
  const lists = [names, dobs, ids, phones, sexes, addresses].map((items) =>
    [...items].sort()
  );
  const canonical = lists.map((items) => items.join('|')).join('\n').trim();
  return sha256(canonical);
};

const computeFileHash = async (file) => {
  if (!file) return null;
  const arrayBuffer = await file.arrayBuffer();
  return sha256(new Uint8Array(arrayBuffer));
};

const decisionKey = (rowId) => `decision_${rowId}`;

const parseResponseError = async (resp) => {
  try {
    const payload = await resp.json();
    if (payload && typeof payload === 'object' && 'detail' in payload) {
      return String(payload.detail);
    }
    return JSON.stringify(payload);
  } catch (e) {
    try {
      const text = await resp.text();
      return text;
    } catch {
      return `HTTP ${resp.status}`;
    }
  }
};

const getPagesText = (row) => {
  const pages = (row.pages || [row.page || 1]).map((p) => parseInt(p, 10)).filter(Boolean);
  if (!pages.length) return '';
  if (pages.length === 1) return `Page ${pages[0]}`;
  return `Pages ${pages.join(', ')}`;
};

const PIIWasher = ({ auth_token, member_id }) => {
  const fileInputRef = useRef(null);
  const backendUrl = DEFAULT_BACKEND_URL;

  // Message-event based auth (iframe mode): use props or data received via postMessage
  const [effectiveMemberId, setEffectiveMemberId] = useState(
    () => member_id || localStorage.getItem('member_id') || ''
  );
  const [effectiveAuthToken, setEffectiveAuthToken] = useState(
    () => auth_token || localStorage.getItem('auth_token') || ''
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [loadingMember, setLoadingMember] = useState(false);
  const [memberError, setMemberError] = useState(null);

  const [namesInput, setNamesInput] = useState('');
  const [dobInput, setDobInput] = useState('');
  const [idInput, setIdInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [sexInput, setSexInput] = useState('');
  const [addressInput, setAddressInput] = useState('');

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);

  const [analysis, setAnalysis] = useState(null);
  const [rows, setRows] = useState([]);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    if (!effectiveAuthToken || !effectiveMemberId) return;

    let cancelled = false;
    const fetchMember = async () => {
      setLoadingMember(true);
      setMemberError(null);
      try {
        const resp = await fetch(`${config.apiUrl}/members/${effectiveMemberId}`, {
          headers: {
            Authorization: `Bearer ${effectiveAuthToken}`,
            Accept: 'application/json'
          }
        });
        if (!resp.ok) throw new Error(`Failed to load member (${resp.status})`);
        const m = await resp.json();
        if (cancelled) return;

        const nameLines = [];
        if (m.First_name) nameLines.push(m.First_name);
        if (m.Surname) nameLines.push(m.Surname);
        if (m.First_name && m.Surname) nameLines.push(`${m.First_name} ${m.Surname}`);
        setNamesInput(nameLines.join('\n'));

        setDobInput(m.Date_of_Birth || '');
        setIdInput([m.ID_Number, m.medicalaid_number].filter(Boolean).join('\n'));

        const phones = [m.Cell, m.OtherTel].filter(Boolean);
        setPhoneInput(phones.join('\n'));

        setSexInput(m.Sex || '');

        const addresses = [m.HomeAdd, m.WorkAdd].filter(Boolean);
        setAddressInput(addresses.join('\n'));
      } catch (err) {
        console.error('Error fetching member:', err);
        if (!cancelled) setMemberError(err.message || 'Failed to load member details.');
      } finally {
        if (!cancelled) setLoadingMember(false);
      }
    };

    fetchMember();
    return () => { cancelled = true; };
  }, [effectiveAuthToken, effectiveMemberId]);

  const [outputPdfBlob, setOutputPdfBlob] = useState(null);
  const [outputFileName, setOutputFileName] = useState(null);

  const [lastPdfHash, setLastPdfHash] = useState(null);
  const [lastPiiHash, setLastPiiHash] = useState(null);
  const [lastBackendUrl, setLastBackendUrl] = useState(null);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isRedacting, setIsRedacting] = useState(false);

  const [analyzeError, setAnalyzeError] = useState(null);
  const [redactError, setRedactError] = useState(null);

  const [decisions, setDecisions] = useState({});
  const [activeReviewTab, setActiveReviewTab] = useState('known');

  // Listen for postMessage (e.g. from parent when embedded in iframe)
  useEffect(() => {
    const allowedOrigins = [
      'https://www.infinity-mc-hms.com',
      'https://infinity-mc-healthplan-edit-dot-infinity-medical-concier-724b0.lm.r.appspot.com',
      'http://localhost:3000',
      'http://localhost:5000',
      'https://localhost:3000',
      'https://localhost:5000'
    ];

    const handleMessage = (event) => {
      if (
        event.data?.target === 'metamask-contentscript' ||
        event.data?.target === 'metamask-inpage' ||
        event.data?.type === 'webpackWarnings'
      ) {
        return;
      }
      if (!allowedOrigins.includes(event.origin)) return;
      if (event.data && event.data.type === 'AUTH_DATA') {
        const { member_id: receivedMemberId, auth_token: receivedAuthToken } = event.data;
        if (receivedMemberId && receivedAuthToken) {
          localStorage.setItem('member_id', receivedMemberId);
          localStorage.setItem('auth_token', receivedAuthToken);
          setEffectiveMemberId(receivedMemberId);
          setEffectiveAuthToken(receivedAuthToken);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    if (!effectiveMemberId || !effectiveAuthToken) {
      allowedOrigins.forEach((origin) => {
        try {
          window.parent.postMessage({ type: 'REQUEST_AUTH' }, origin);
        } catch (_) {}
      });
    }
    return () => window.removeEventListener('message', handleMessage);
  }, [effectiveMemberId, effectiveAuthToken]);

  const resetAnalysisState = () => {
    setAnalysis(null);
    setRows([]);
    setMatches([]);
    setOutputPdfBlob(null);
    setOutputFileName(null);
    setLastPdfHash(null);
    setLastPiiHash(null);
    setLastBackendUrl(null);
    setDecisions({});
  };

  const fetchAuthHeaders = useCallback(async () => {
    return {};
  }, []);

  const currentAcceptedRowIds = useCallback(
    (rowsToUse) => {
      if (!rowsToUse || !rowsToUse.length) return [];
      return rowsToUse
        .map((row) => parseInt(row.row_id, 10))
        .filter((id) => {
          const key = decisionKey(id);
          const decision = decisions[key] || 'Accept';
          return decision === 'Accept';
        });
    },
    [decisions]
  );

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setUploadedFile(file);
    setUploadedFileName(file ? file.name : null);
    if (!file) {
      resetAnalysisState();
    } else {
      resetAnalysisState();
    }
  };

  const handleAnalyze = useCallback(
    async (forceReprocess = false) => {
      if (!uploadedFile) return false;

      setAnalyzeError(null);
      setRedactError(null);

      const names = parseLines(namesInput);
      const dobs = parseLines(dobInput);
      const ids = parseLines(idInput);
      const phones = parseLines(phoneInput);
      const sexes = parseLines(sexInput);
      const addresses = parseLines(addressInput);

      try {
        setIsAnalyzing(true);

        const [piiHash, pdfHash] = await Promise.all([
          hashPiiInputs(names, dobs, ids, phones, sexes, addresses),
          computeFileHash(uploadedFile)
        ]);

        const backendTrimmed = (backendUrl || '').trim();

        const needsAnalysis =
          !analysis ||
          !lastPdfHash ||
          !lastPiiHash ||
          !lastBackendUrl ||
          lastPdfHash !== pdfHash ||
          lastPiiHash !== piiHash ||
          lastBackendUrl !== backendTrimmed ||
          forceReprocess;

        if (!needsAnalysis) {
          setIsAnalyzing(false);
          return true;
        }

        const formData = new FormData();
        formData.append('file', uploadedFile, uploadedFile.name);
        formData.append('names', names.join('\n'));
        formData.append('dobs', dobs.join('\n'));
        formData.append('ids', ids.join('\n'));
        formData.append('phones', phones.join('\n'));
        formData.append('sexes', sexes.join('\n'));
        formData.append('addresses', addresses.join('\n'));
        formData.append('detect_additional_pii', 'true');
        formData.append('enable_debug', 'true');

        const headers = await fetchAuthHeaders();

        const resp = await fetch(`${backendTrimmed.replace(/\/+$/, '')}/analyze`, {
          method: 'POST',
          body: formData,
          headers
        });

        if (!resp.ok) {
          const msg = await parseResponseError(resp);
          throw new Error(`Analyze failed (${resp.status}): ${msg}`);
        }

        const payload = await resp.json();
        const newRows = Array.isArray(payload.rows) ? payload.rows : [];
        const newMatches = Array.isArray(payload.matches) ? payload.matches : [];

        setAnalysis(payload);
        setRows(newRows);
        setMatches(newMatches);
        setUploadedFileName(uploadedFile.name);
        setLastPdfHash(pdfHash);
        setLastPiiHash(piiHash);
        setLastBackendUrl(backendTrimmed);
        setOutputPdfBlob(null);
        setOutputFileName(null);

        const initialDecisions = {};
        newRows.forEach((row) => {
          const id = parseInt(row.row_id, 10);
          if (!Number.isNaN(id)) {
            initialDecisions[decisionKey(id)] = row.redact ? 'Accept' : 'Reject';
          }
        });
        setDecisions(initialDecisions);
        return true;
      } catch (err) {
        console.error('Error during analyze:', err);
        const isNetworkOrCors =
          err.name === 'TypeError' && (err.message === 'Failed to fetch' || err.message.includes('Load failed'));
        const message = isNetworkOrCors
          ? 'Request blocked by the browser (likely CORS). Ensure the API allows this origin.'
          : (err.message || 'Failed to analyze PDF.');
        setAnalyzeError(message);
        setAnalysis(null);
        setRows([]);
        setMatches([]);
        return false;
      } finally {
        setIsAnalyzing(false);
      }
    },
    [
      uploadedFile,
      namesInput,
      dobInput,
      idInput,
      phoneInput,
      sexInput,
      addressInput,
      backendUrl,
      analysis,
      lastPdfHash,
      lastPiiHash,
      lastBackendUrl,
      fetchAuthHeaders
    ]
  );

  useEffect(() => {
    if (uploadedFile) {
      handleAnalyze(false);
    }
  }, [uploadedFile]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when file is selected; handleAnalyze has many deps

  const handleDecisionChange = (rowId, value) => {
    const key = decisionKey(rowId);
    setDecisions((prev) => ({ ...prev, [key]: value }));
  };

  const knownRows = useMemo(() => rows.filter((row) => row.source === 'known-input'), [rows]);
  const autoRows = useMemo(() => rows.filter((row) => row.source === 'auto-detect'), [rows]);

  const handleRedact = useCallback(async () => {
    if (!uploadedFile || !rows.length) return;

    setRedactError(null);

    const acceptedRowIds = currentAcceptedRowIds(rows);
    if (!acceptedRowIds.length) {
      setRedactError('No matches accepted for redaction.');
      return;
    }

    try {
      setIsRedacting(true);

      const formData = new FormData();
      formData.append('file', uploadedFile, uploadedFile.name);
      formData.append('matches_json', JSON.stringify(matches));
      formData.append('rows_json', JSON.stringify(rows));
      formData.append('accepted_row_ids_json', JSON.stringify(acceptedRowIds));

      const backendTrimmed = (backendUrl || '').trim();
      const headers = await fetchAuthHeaders();

      const resp = await fetch(`${backendTrimmed.replace(/\/+$/, '')}/redact`, {
        method: 'POST',
        body: formData,
        headers
      });

      if (!resp.ok) {
        const msg = await parseResponseError(resp);
        throw new Error(`Redaction failed (${resp.status}): ${msg}`);
      }

      const blob = await resp.blob();
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const memberId = effectiveMemberId || 'unknown';
      const random4 = String(Math.floor(1000 + Math.random() * 9000));
      const outName = `${dateStr}_${memberId}_${random4}.pdf`;

      setOutputPdfBlob(blob);
      setOutputFileName(outName);
    } catch (err) {
      console.error('Error during redaction:', err);
      setRedactError(err.message || 'Failed to create redacted PDF.');
      setOutputPdfBlob(null);
      setOutputFileName(null);
    } finally {
      setIsRedacting(false);
    }
  }, [
    uploadedFile,
    uploadedFileName,
    rows,
    matches,
    backendUrl,
    currentAcceptedRowIds,
    fetchAuthHeaders,
    effectiveMemberId
  ]);

  const handleDownload = () => {
    if (!outputPdfBlob || !outputFileName) return;
    const url = window.URL.createObjectURL(outputPdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = outputFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const pagesMetric = analysis ? Number(analysis.pages || 0) : 0;
  const knownCountMetric = analysis ? Number(analysis.known_count || 0) : 0;
  const suggestedCountMetric = analysis ? Number(analysis.suggested_count || 0) : 0;

  const canAdvanceFromStep2 = !!analysis && !isAnalyzing;

  const goNext = () => {
    if (currentStep < 4) setCurrentStep((s) => s + 1);
  };
  const goBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  return (
    <IonPage>
      <IonContent className="pii-washer-page-content">
        <div className="pii-washer-container"> 

          {/* Stepper bar */}
          <div className="pii-stepper">
            {STEPS.map((step) => {
              const isActive = step.number === currentStep;
              const isCompleted = step.number < currentStep;
              let className = 'pii-step';
              if (isActive) className += ' pii-step-active';
              if (isCompleted) className += ' pii-step-completed';

              return (
                <button
                  key={step.number}
                  type="button"
                  className={className}
                  onClick={() => {
                    if (step.number <= currentStep) setCurrentStep(step.number);
                  }}
                  disabled={step.number > currentStep}
                >
                  <span className="pii-step-number">
                    {isCompleted ? (
                      <IonIcon icon={checkmarkCircleOutline} />
                    ) : (
                      step.number
                    )}
                  </span>
                  <span className="pii-step-label">{step.label}</span>
                </button>
              );
            })}
          </div>

          {/* Step 1: Enter PII */}
          {currentStep === 1 && (
            <div className="pii-section">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Enter Known PII</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <p className="pii-section-subtitle">
                    Enter the PII values you want to find and redact. One entry per line for each field.
                  </p>
                  {loadingMember && (
                    <div className="pii-analysis-done" style={{ marginBottom: 12 }}>
                      <IonSpinner name="crescent" />
                      <IonText>Loading member details...</IonText>
                    </div>
                  )}
                  {memberError && (
                    <IonCard color="danger" className="pii-error-card" style={{ marginBottom: 12 }}>
                      <IonCardContent>
                        <IonText color="light">
                          <strong>Error:</strong> {memberError}
                        </IonText>
                      </IonCardContent>
                    </IonCard>
                  )}
                  <div className="pii-input-grid">
                    <IonItem>
                      <IonLabel position="stacked">Names (one per line)</IonLabel>
                      <IonTextarea
                        className="pii-textarea"
                        autoGrow
                        value={namesInput}
                        onIonInput={(e) => setNamesInput(e.detail.value || '')}
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">DOBs (one per line)</IonLabel>
                      <IonTextarea
                        className="pii-textarea"
                        autoGrow
                        value={dobInput}
                        onIonInput={(e) => setDobInput(e.detail.value || '')}
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">ID / Member Numbers (one per line)</IonLabel>
                      <IonTextarea
                        className="pii-textarea"
                        autoGrow
                        value={idInput}
                        onIonInput={(e) => setIdInput(e.detail.value || '')}
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">Phone Numbers (one per line)</IonLabel>
                      <IonTextarea
                        className="pii-textarea"
                        autoGrow
                        value={phoneInput}
                        onIonInput={(e) => setPhoneInput(e.detail.value || '')}
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">Sex (one per line)</IonLabel>
                      <IonTextarea
                        className="pii-textarea"
                        autoGrow
                        value={sexInput}
                        onIonInput={(e) => setSexInput(e.detail.value || '')}
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">Addresses (one per line)</IonLabel>
                      <IonTextarea
                        className="pii-textarea"
                        autoGrow
                        value={addressInput}
                        onIonInput={(e) => setAddressInput(e.detail.value || '')}
                      />
                    </IonItem>
                  </div>
                </IonCardContent>
              </IonCard>

              <div className="pii-nav-buttons">
                <div />
                <IonButton color="primary" onClick={goNext}>
                  Continue
                  <IonIcon icon={arrowForwardOutline} slot="end" />
                </IonButton>
              </div>
            </div>
          )}

          {/* Step 2: Upload PDF */}
          {currentStep === 2 && (
            <div className="pii-section">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Upload PDF</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="pii-upload-row">
                    <div>
                      <IonButton
                        color="primary"
                        fill="outline"
                        onClick={() => {
                          if (fileInputRef.current) fileInputRef.current.click();
                        }}
                      >
                        <IonIcon icon={documentTextOutline} slot="start" />
                        Choose PDF
                      </IonButton>
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                        onChange={handleFileChange}
                      />
                      {uploadedFileName && (
                        <div className="pii-upload-filename">
                          Selected: <strong>{uploadedFileName}</strong>
                        </div>
                      )}
                    </div>

                    {uploadedFile && (
                      <div className="pii-actions-row">
                        <IonButton
                          color="primary"
                          fill="outline"
                          onClick={() => handleAnalyze(true)}
                          disabled={isAnalyzing}
                        >
                          {isAnalyzing ? (
                            <>
                              <IonSpinner name="crescent" slot="start" />
                              Analyzing...
                            </>
                          ) : (
                            'Re-analyze'
                          )}
                        </IonButton>
                      </div>
                    )}

                    {analyzeError && (
                      <IonCard color="danger" className="pii-error-card">
                        <IonCardContent>
                          <IonText color="light">
                            <strong>Error:</strong> {analyzeError}
                          </IonText>
                        </IonCardContent>
                      </IonCard>
                    )}

                    {analysis && !isAnalyzing && (
                      <div className="pii-analysis-done">
                        <IonIcon icon={checkmarkCircleOutline} color="success" />
                        <IonText color="success">
                          Analysis complete &mdash; {pagesMetric} page{pagesMetric !== 1 ? 's' : ''}, {knownCountMetric} known match{knownCountMetric !== 1 ? 'es' : ''}, {suggestedCountMetric} auto-detected.
                        </IonText>
                      </div>
                    )}
                  </div>
                </IonCardContent>
              </IonCard>

              <div className="pii-nav-buttons">
                <IonButton fill="outline" color="medium" onClick={goBack}>
                  <IonIcon icon={arrowBackOutline} slot="start" />
                  Back
                </IonButton>
                <IonButton
                  color="primary"
                  onClick={goNext}
                  disabled={!canAdvanceFromStep2}
                >
                  Continue
                  <IonIcon icon={arrowForwardOutline} slot="end" />
                </IonButton>
              </div>
            </div>
          )}

          {/* Step 3: Review PII */}
          {currentStep === 3 && (
            <div className="pii-section">
              {/* Metrics summary */}
              <div className="pii-metrics-grid">
                <IonCard className="pii-metric-card">
                  <IonCardContent>
                    <div className="pii-metric-label">Pages</div>
                    <div className="pii-metric-value">{pagesMetric}</div>
                  </IonCardContent>
                </IonCard>
                <IonCard className="pii-metric-card">
                  <IonCardContent>
                    <div className="pii-metric-label">Known PII Matches</div>
                    <div className="pii-metric-value">{knownCountMetric}</div>
                  </IonCardContent>
                </IonCard>
                <IonCard className="pii-metric-card">
                  <IonCardContent>
                    <div className="pii-metric-label">Suggested Extra PII</div>
                    <div className="pii-metric-value">{suggestedCountMetric}</div>
                  </IonCardContent>
                </IonCard>
              </div>

              {analysis?.debug && (
                <details className="pii-debug-details">
                  <summary>Debug logs</summary>
                  <div className="pii-debug-inner">
                    <div className="pii-debug-block">
                      <IonLabel><strong>DOB compact targets</strong></IonLabel>
                      <pre className="pii-debug-code">
                        {(analysis.debug.dob_compact_targets || []).join('\n') || '(none)'}
                      </pre>
                    </div>
                    <div className="pii-debug-block">
                      <IonLabel><strong>DOB-related events</strong></IonLabel>
                      <pre className="pii-debug-json">
                        {JSON.stringify(analysis.debug.events || [], null, 2)}
                      </pre>
                    </div>
                  </div>
                </details>
              )}

              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Review Detected PII</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {!rows.length && (
                    <IonText color="success">
                      No matches found with current PII inputs and backend rules.
                    </IonText>
                  )}
                  {!!rows.length && (
                    <>
                      <p className="pii-section-subtitle">
                        Rows marked for redaction default to <strong>Accept</strong>. Toggle each card as needed.
                      </p>
                      <div className="pii-review-tabs">
                        <IonSegment
                          value={activeReviewTab}
                          onIonChange={(e) => setActiveReviewTab(e.detail.value)}
                        >
                          <IonSegmentButton value="known">
                            <IonLabel>Known Input ({knownRows.length})</IonLabel>
                          </IonSegmentButton>
                          <IonSegmentButton value="auto">
                            <IonLabel>Auto Detect ({autoRows.length})</IonLabel>
                          </IonSegmentButton>
                        </IonSegment>
                      </div>

                      <div className="pii-cards-grid">
                        {(activeReviewTab === 'known' ? knownRows : autoRows).map((row) => {
                          const rowId = parseInt(row.row_id, 10);
                          const key = decisionKey(rowId);
                          const decision = decisions[key] || (row.redact ? 'Accept' : 'Reject');
                          const pagesText = getPagesText(row);

                          return (
                            <IonCard key={rowId}>
                              <IonCardContent>
                                <div className="pii-card-header">
                                  <div className="pii-card-label">{row.label}</div>
                                  <div className="pii-card-meta">
                                    {pagesText}
                                    {row.source ? ` | Source: ${row.source}` : ''}
                                  </div>
                                </div>
                                <div className="pii-card-text" style={{ marginTop: 8 }}>
                                  {String(row.text || '')}
                                </div>
                                <div className="pii-decision-toggle">
                                  <IonSegment
                                    value={decision}
                                    onIonChange={(e) => handleDecisionChange(rowId, e.detail.value)}
                                  >
                                    <IonSegmentButton value="Accept">
                                      <IonLabel>Accept</IonLabel>
                                    </IonSegmentButton>
                                    <IonSegmentButton value="Reject">
                                      <IonLabel>Reject</IonLabel>
                                    </IonSegmentButton>
                                  </IonSegment>
                                </div>
                              </IonCardContent>
                            </IonCard>
                          );
                        })}
                      </div>
                    </>
                  )}
                </IonCardContent>
              </IonCard>

              <div className="pii-nav-buttons">
                <IonButton fill="outline" color="medium" onClick={goBack}>
                  <IonIcon icon={arrowBackOutline} slot="start" />
                  Back
                </IonButton>
                <IonButton color="primary" onClick={goNext}>
                  Continue
                  <IonIcon icon={arrowForwardOutline} slot="end" />
                </IonButton>
              </div>
            </div>
          )}

          {/* Step 4: Redact & Download */}
          {currentStep === 4 && (
            <div className="pii-section">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Redact & Download</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <p className="pii-section-subtitle">
                    Generate a redacted PDF with your accepted PII matches removed.
                  </p>

                  <div className="pii-redact-actions">
                    <IonButton
                      color="primary"
                      onClick={handleRedact}
                      disabled={!rows.length || isRedacting}
                    >
                      {isRedacting ? (
                        <>
                          <IonSpinner name="crescent" slot="start" />
                          Creating Redacted PDF...
                        </>
                      ) : (
                        'Create Redacted PDF'
                      )}
                    </IonButton>

                    {redactError && (
                      <IonText color="danger" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8 }}>
                        <IonIcon icon={alertCircleOutline} />
                        {redactError}
                      </IonText>
                    )}

                    {outputPdfBlob && outputFileName && (
                      <div className="pii-download-section">
                        <IonIcon icon={checkmarkCircleOutline} color="success" style={{ fontSize: 24 }} />
                        <IonText color="success" style={{ fontWeight: 600 }}>
                          Redacted PDF created successfully.
                        </IonText>
                        <IonButton color="success" onClick={handleDownload}>
                          <IonIcon icon={downloadOutline} slot="start" />
                          Download {outputFileName}
                        </IonButton>
                      </div>
                    )}
                  </div>
                </IonCardContent>
              </IonCard>

              <div className="pii-nav-buttons">
                <IonButton fill="outline" color="medium" onClick={goBack}>
                  <IonIcon icon={arrowBackOutline} slot="start" />
                  Back
                </IonButton>
                <div />
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PIIWasher;
